import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatOption,
  MatSelect,
  MatSelectModule,
} from '@angular/material/select';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BaseSearchOptions,
  BaseSelectViewModel,
  WorkingSpaceViewModel,
} from '@core/api';
import { RoutePath } from '@core/routing/routing.types';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { EditWorkingSpaceService } from '@core/services/edit-working-space/edit-working-space.service';
import { UserService } from '@core/services/oauth-service/user.service';
import {
  SortDirection,
  SortService,
} from '@core/services/sort-service/sort.service';
import { MtxGridColumn, MtxGridModule } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import {
  ITableConfig,
  ITableGridConfig,
  ITablePagingConfig,
} from '@shared/components/table-base/table-base.types';
import { UserRoles } from '@shared/constants/user-roles.constants';
import { SharedModule } from '@shared/shared.module';
import { rxSubscriptionContainerMixin } from '@shared/utils/rx-subscription-container.mixin';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { startWith } from 'rxjs/operators';
import { SafeHtmlPipe } from '../../../../../app/shared/pipes/safe-html.pipe';

export interface IWorkingSpaceViewModel {
  isInactive: boolean;
  APNo: number;
  name: string;
  dummy: boolean;
  dummyKind: string;
  areaType: number;
}

@Component({
  selector: 'app-working-space',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SmartusComponentsModule,
    MtxGridModule,
    TranslateModule,
    MatInputModule,
    MatSelect,
    MatOption,
    MatTooltip,
    MatCheckboxModule,
    MatSelectModule,
    MatTooltipModule,
    SmartusComponentsModule,
    SafeHtmlPipe,
    OverrideModule,
    SharedModule,
    MatAutocompleteModule,
    MatIconModule,
  ],
  templateUrl: './working-space.component.html',
  styleUrl: './working-space.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkingSpaceComponent
  extends rxSubscriptionContainerMixin()
  implements OnInit, AfterViewInit
{
  @Input() workspaceId: string;
  @Input() isEmbedded: boolean = false;
  @Output() closeEmbeddedView = new EventEmitter<void>();
  @Output() componentLoaded = new EventEmitter<void>();

  headline = this.translateService.instant('locationServices.title');

  private editWorkingSpaceService = inject(EditWorkingSpaceService);
  private cd = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);

  buildingId: string;
  stageId: string;
  spaceId: string;
  roomId: string;
  subSpaceId: string;

  changeReasons: BaseSelectViewModel[] = [];
  loadedInformation = false;
  isLoading = false;
  currentRoom = '';
  numberOfWorkingPlaces = 0;
  isDataManager = false;
  frozenZoneStartDate = '';
  frozenZoneEndDate = '';
  frozenStatus: boolean = false;
  description = '';
  selectedChangeReasonId = '';
  spaceNumber: string = '';
  isWmProdManagement: boolean = false;

  workingSpaceListSubject: BehaviorSubject<WorkingSpaceViewModel[]> =
    new BehaviorSubject<WorkingSpaceViewModel[]>([]);
  workingSpacesToAdd: WorkingSpaceViewModel[] = [];
  workingSpaceList$ = this.workingSpaceListSubject.asObservable();
  originalWorkingSpaces: WorkingSpaceViewModel[] = [];

  @ViewChild('checkboxInactiveTemplate', { static: true })
  checkboxInactiveTemplateRef!: TemplateRef<void>;

  @ViewChild('checkboxDummyTemplate', { static: true })
  checkboxDummyTemplateRef!: TemplateRef<void>;

  @ViewChild('inputAPNoTemplate', { static: true })
  inputAPNoTemplateRef!: TemplateRef<void>;

  @ViewChild('inputNameTemplate', { static: true })
  inputNameTemplateRef!: TemplateRef<void>;

  @ViewChild('selectDummyKindTemplate', { static: true })
  selectDummyKindTemplateRef!: TemplateRef<void>;

  @ViewChild('selectAreaTypeTemplate', { static: true })
  selectAreaTypeTemplateRef!: TemplateRef<void>;

  config: ITableConfig = <ITableConfig>{
    grid: <ITableGridConfig>{
      showToolbar: true,
      columnResizable: false,
      paging: <ITablePagingConfig>{
        pageIndex: 0,
        pageSize: 50,
        showPaginator: true,
      },
    },
  };

  query: BaseSearchOptions = {
    pagingOptions: {
      skip:
        this.config.grid.paging.pageIndex * this.config.grid.paging.pageSize,
      take: this.config.grid.paging.pageSize,
    },
  };

  columns: MtxGridColumn[] = [];
  officeAreaTypes: BaseSelectViewModel[] = [];
  activeToast: ActiveToast<object>;

  dummyTypes: BaseSelectViewModel[] = [];

  loadingData: boolean = false;

  changeReasonSearchControl = new FormControl('');
  filteredChangeReasons: Observable<BaseSelectViewModel[]>;

  get changeReasonFieldConfig(): FormlyFieldConfig {
    return {
      props: {
        label: this.translateService.instant('locationServices.changeReason'),
        options: this.changeReasons.map(changeReason => ({
          value: changeReason.id,
          label: changeReason.name,
        })),
        disabled: this.frozenStatus,
        placeholder: this.translateService.instant('shared.select'),
        required: true,
      },
    };
  }

  constructor(
    private translateService: TranslateService,
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    private userService: UserService,
    private toastr: ToastrService
  ) {
    super();

    this.initializeIds();

    this.pushSubscription(
      this.userService
        .checkIfUserHasAnyRole([
          UserRoles.SMARTPORTAL_MODULE_HELPDESK,
          UserRoles.SMARTPORTAL_MODULE_LOCATION,
          UserRoles.SMARTPORTAL_LOCATION_DATA_MANAGER,
          UserRoles.SMARTPORTAL_OCCUPANCY_CREATE,
          UserRoles.SMARTPORTAL_AREAREQUEST_CREATE,
          UserRoles.SMARTPORTAL_AREARETURN_CREATE,
          UserRoles.SMARTPORTAL_RELOCATION_CREATE,
          UserRoles.SMARTPORTAL_REM_DATENPFLEGE,
        ])
        .subscribe(hasRole => (this.isDataManager = hasRole))
    );

    this.changeReasonSearchControl.valueChanges.subscribe(value => {
      this.selectedChangeReasonId = value || '';
    });
  }

  private initializeIds(): void {
    this.buildingId = this.route.snapshot.paramMap.get('buildingId');
    this.stageId = this.route.snapshot.paramMap.get('stageId');
    this.spaceId = this.route.snapshot.paramMap.get('spaceId');
    this.roomId = this.route.snapshot.paramMap.get('roomId');

    if (this.workspaceId && this.workspaceId.includes('/')) {
      const idParts = this.workspaceId.split('/');

      if (idParts.length >= 4) {
        if (!this.buildingId) this.buildingId = idParts[0];
        if (!this.stageId) this.stageId = idParts[1];
        if (!this.spaceId) this.spaceId = idParts[2];
        if (!this.roomId) this.roomId = idParts[3];
      }
    }

    this.subSpaceId = `${this.buildingId}/${this.stageId}/${this.spaceId}/${this.roomId}`;
  }

  ngOnInit(): void {
    this.loadingData = true;

    if (this.isEmbedded && this.workspaceId) {
      this.initializeIds();
    }

    if (!this.isEmbedded) {
      this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);
    }

    // Load initial workspace data
    this.loadWorkspaceData()
      .pipe(
        switchMap(() => {
          return forkJoin({
            frozenZoneStatus: this.editWorkingSpaceService
              .checkFrozenZone()
              .pipe(catchError(() => of({ startDate: '', endDate: '' }))),
            changeReasons: this.editWorkingSpaceService
              .getChangeReasons()
              .pipe(catchError(() => of([]))),
            officeAreaTypes: this.editWorkingSpaceService
              .getOfficeAreaTypes()
              .pipe(catchError(() => of([]))),
            roomInformation: this.editWorkingSpaceService
              .getRoomInformation(
                this.subSpaceId.split('/').slice(0, 3).join('/')
              )
              .pipe(
                catchError(() => of({ room: '', isWmProdManagement: false }))
              ),
            dummyTypes: this.editWorkingSpaceService
              .getDummyTypes()
              .pipe(catchError(() => of([]))),
          });
        }),
        finalize(() => {
          this.loadingData = false;
          if (this.isEmbedded) {
            setTimeout(() => {
              this.componentLoaded.emit();
            }, 100);
          }
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: results => {
          this.changeReasons = results.changeReasons;
          this.officeAreaTypes = results.officeAreaTypes;
          this.spaceNumber = results.roomInformation.room;
          this.isWmProdManagement = results.roomInformation.isWmProdManagement;
          this.dummyTypes = results.dummyTypes;

          this.currentRoom =
            this.spaceNumber !== undefined
              ? (this.route.snapshot.paramMap.get('spaceId') ?? this.spaceId) +
                ' / ' +
                this.spaceNumber
              : (this.route.snapshot.paramMap.get('spaceId') ?? this.spaceId);

          const frozenZoneData = results.frozenZoneStatus;
          if (
            frozenZoneData?.startDate.length > 0 &&
            frozenZoneData?.endDate.length > 0
          ) {
            this.frozenZoneStartDate = frozenZoneData.startDate;
            this.frozenZoneEndDate = frozenZoneData.endDate;
            this.frozenStatus = true;
            this.description = this.translateService.instant(
              'data_maintenance.frozenZone',
              {
                frozenZoneStartDate: this.frozenZoneStartDate,
                frozenZoneEndDate: this.frozenZoneEndDate,
              }
            );
          } else {
            this.frozenStatus = false;
          }

          this.initializeColumns();

          this.cd.detectChanges();

          this.filteredChangeReasons =
            this.changeReasonSearchControl.valueChanges.pipe(
              startWith(''),
              map(value => this.filterChangeReasons(value || ''))
            );
        },
        error: error => {
          console.error('Error loading workspace data:', error);
          if (this.isEmbedded) {
            this.componentLoaded.emit();
          }
        },
      });
  }

  private loadWorkspaceData(): Observable<any> {
    const workspaceDataSource = this.isEmbedded
      ? this.loadWorkspaceDataForEmbedded()
      : this.loadWorkspaceDataFromRoute();

    return workspaceDataSource.pipe(
      map(workspaceData => {
        if (workspaceData) {
          const sortedWorkspaceData = SortService.sort(
            [...workspaceData],
            SortDirection.Ascending,
            'name'
          );

          this.workspaceId = workspaceData.workspaceId || this.workspaceId;
          this.workingSpaceListSubject.next(sortedWorkspaceData);
          this.originalWorkingSpaces = JSON.parse(
            JSON.stringify(sortedWorkspaceData)
          );
          this.numberOfWorkingPlaces = this.originalWorkingSpaces.filter(
            room => room.isActive
          ).length;
        }
        return workspaceData;
      })
    );
  }

  private initializeColumns(): void {
    this.columns = [
      {
        header: this.translateService.instant(
          'locationServices.workingSpaceTable.active'
        ),
        field: 'isActive',
        sortable: true,
        cellTemplate: this.checkboxInactiveTemplateRef,
      },
      {
        header: this.translateService.instant(
          'locationServices.workingSpaceTable.dummy'
        ),
        field: 'dummy',
        sortable: true,
        cellTemplate: this.checkboxDummyTemplateRef,
      },
      {
        header: this.translateService.instant(
          'locationServices.workingSpaceTable.aPNo'
        ),
        field: 'apNo',
        sortable: true,
        cellTemplate: this.inputAPNoTemplateRef,
      },
      {
        header: this.translateService.instant(
          'locationServices.workingSpaceTable.name'
        ),
        field: 'name',
        sortable: true,
        cellTemplate: this.inputNameTemplateRef,
      },
      {
        header: this.translateService.instant(
          'locationServices.workingSpaceTable.dummyKind'
        ),
        field: 'dummyKind',
        sortable: true,
        cellTemplate: this.selectDummyKindTemplateRef,
      },
      {
        header: this.translateService.instant('Magenta Office Flächenart'),
        field: 'areaType',
        sortable: true,
        cellTemplate: this.selectAreaTypeTemplateRef,
      },
    ];
  }

  private loadWorkspaceDataForEmbedded(): Observable<any> {
    if (!this.workspaceId) return of(null);

    this.loadingData = true;
    return this.editWorkingSpaceService.getWorkingSpacesByAreaCode(
      this.workspaceId
    );
  }

  private loadWorkspaceDataFromRoute(): Observable<any> {
    return this.route.data.pipe(map(data => data.roomData));
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const headers = document.querySelectorAll('.mtx-grid .mat-sort-header');
      headers.forEach(header => {
        (header as HTMLElement).setAttribute('tabindex', '0');
      });
    });
  }

  public getActiveWorkingPlaces(): number {
    return this.workingSpaceListSubject.value.filter(room => room.isActive)
      .length;
  }

  public addWorkingSpace() {
    this.toastr.remove(this.activeToast?.toastId);
    let hasNonNullValue = false;

    if (this.getActiveWorkingPlaces() === 0) {
      hasNonNullValue = true;
    } else {
      hasNonNullValue = Object.values(
        this.workingSpaceListSubject.value[this.getActiveWorkingPlaces() - 1]
      ).some(value => value !== null);
    }

    if (hasNonNullValue) {
      if (this.numberOfWorkingPlaces < this.getActiveWorkingPlaces()) {
        return;
      }

      const workingPlacesToAdd =
        this.numberOfWorkingPlaces - this.getActiveWorkingPlaces() === 0
          ? 1
          : this.numberOfWorkingPlaces - this.getActiveWorkingPlaces();

      if (workingPlacesToAdd + this.getActiveWorkingPlaces() >= 100) {
        return;
      }

      for (let i = 0; i < workingPlacesToAdd; i++) {
        const maxApNo = this.workingSpaceListSubject.value.reduce(
          (max, item) => {
            const currentApNo =
              item.apNo !== null ? parseInt(item.apNo, 10) : 0;
            return currentApNo > max ? currentApNo : max;
          },
          0
        );

        let maxName = this.workingSpaceListSubject.value.reduce((max, item) => {
          const namePart = item.name || '';
          const match = namePart.match(/(\d+)$/);
          const currentNumber = match ? parseInt(match[1], 10) : 0;
          const maxNumber = max.match(/(\d+)$/)
            ? parseInt(max.match(/(\d+)$/)[1], 10)
            : 0;

          return currentNumber > maxNumber ? namePart : max;
        }, '');

        if (maxName === '') {
          maxName = `${this.spaceNumber}-000`;
        }

        const currentNumber = maxName.match(/(\d+)$/)
          ? parseInt(maxName.match(/(\d+)$/)[1], 10)
          : 0;
        const newNumber = (currentNumber + 1).toString();

        const numberLength = maxName.match(/(\d+)$/)
          ? maxName.match(/(\d+)$/)[1].length
          : 1;
        const formattedNewNumber = newNumber.padStart(numberLength, '0');

        const newName = `${maxName.replace(/(\d+)$/, '')}${formattedNewNumber}`;

        const newWorkingSpace = {
          isActive: null,
          apNo: (maxApNo + 1).toString(),
          name: newName,
          isDummy: null,
          dummyKind: null,
          magentaSpaceType: null,
          subSpaceId: this.subSpaceId,
        };

        this.workingSpaceListSubject.next([
          ...this.workingSpaceListSubject.value,
          newWorkingSpace,
        ]);

        this.workingSpacesToAdd = [...this.workingSpacesToAdd, newWorkingSpace];
      }
      this.numberOfWorkingPlaces = this.getActiveWorkingPlaces();
    }
  }

  public save() {
    if (
      this.hasMultipleDummies() === true ||
      this.hasDummyAndMultipleActiveWorkingSpaces() === true
    ) {
      this.toastr.error(
        this.translateService.instant(
          'locationServices.error.only_one_dummyType_accepted'
        )
      );
      return;
    }
    if (this.hasUnsetDummyType() === true) {
      this.toastr.error(
        this.translateService.instant(
          'locationServices.error.no_dummyType_selected'
        )
      );
      return;
    }
    if (
      this.isWmProdManagement === true &&
      this.hasNoActiveWorkingSpace() === true
    ) {
      this.toastr.error(
        this.translateService.instant(
          'locationServices.error.no_active_working_space'
        )
      );
      return;
    }

    this.isLoading = true;

    const updatedWorkingSpaces = this.workingSpaceListSubject.value
      .filter((space, index) => {
        const original = this.originalWorkingSpaces[index];
        const isInWorkingSpacesToAdd = this.workingSpacesToAdd.some(
          ws => ws.apNo === space.apNo && ws.name === space.name
        );
        return (
          !isInWorkingSpacesToAdd &&
          (space.isActive !== original.isActive ||
            space.apNo !== original.apNo ||
            space.name !== original.name ||
            space.isDummy !== original.isDummy ||
            space.dummyKind !== original.dummyKind ||
            space.magentaSpaceType !== original.magentaSpaceType)
        );
      })
      .map(space => ({
        workingSpaceId: space.subSpaceId,
        isActive: space.isActive,
        name: space.name,
        magentaSpaceType: space.magentaSpaceType,
        dummyKind: space.dummyKind,
      }));

    const create$ =
      this.workingSpacesToAdd.length > 0
        ? this.editWorkingSpaceService.createWorkingSpaces(
            this.workingSpacesToAdd.map(space => ({
              ...space,
              changeReason: this.selectedChangeReasonId,
              roomId: this.roomId,
            }))
          )
        : of(null);

    const update$ =
      updatedWorkingSpaces.length > 0
        ? this.editWorkingSpaceService.updateWorkingSpaces({
            updates: updatedWorkingSpaces,
            changeReason: this.selectedChangeReasonId,
            usage: undefined,
          })
        : of(null);

    forkJoin([create$, update$]).subscribe({
      next: () => {
        this.toastr.success(
          this.translateService.instant('locationServices.successfullySaved')
        );
        this.changeReasonSearchControl.reset();
        this.workingSpacesToAdd = [];

        setTimeout(() => {
          this.loadWorkspaceData().subscribe({
            next: () => {
              this.loadingData = false;
              this.cd.detectChanges();
            },
            error: error => {
              console.error('Error reloading workspace data:', error);
              this.loadingData = false;
              this.cd.detectChanges();
            },
          });
        }, 5000);

        this.isLoading = false;
        this.cd.detectChanges();
      },
      error: () => {
        this.toastr.error(
          this.translateService.instant('locationServices.errorOccurred')
        );
        this.isLoading = false;
        this.cd.detectChanges();
      },
    });
  }

  public onCancelClick() {
    if (this.isEmbedded) {
      this.closeEmbeddedView.emit();
    } else {
      this.router.navigateByUrl(`/${RoutePath.DATA_MAINTENANCE}`);
    }
  }

  onCheckboxClick(e: MatCheckboxChange, subSpaceId: string) {
    if (e.checked === false) {
      this.workingSpaceListSubject.next(
        this.workingSpaceListSubject.value.map(item =>
          item.subSpaceId === subSpaceId ? { ...item, dummyKind: null } : item
        )
      );
    }
  }

  hasUnsetDummyType(): boolean {
    return this.workingSpaceListSubject.value.some(
      item =>
        item.isDummy &&
        (item.dummyKind === null || item.dummyKind === undefined)
    );
  }

  hasMultipleDummies(): boolean {
    return (
      this.workingSpaceListSubject.value.filter(item => item.isDummy).length > 1
    );
  }

  hasDummyAndMultipleActiveWorkingSpaces(): boolean {
    const dummyItems = this.workingSpaceListSubject.value.filter(
      item => item.isDummy
    );
    const activeItems = this.workingSpaceListSubject.value.filter(
      item => item.isActive
    );

    return dummyItems.length > 0 && activeItems.length > 1;
  }

  hasNoActiveWorkingSpace(): boolean {
    return (
      this.workingSpaceListSubject.value.filter(item => item.isActive)
        .length === 0
    );
  }

  private filterChangeReasons(value: string): BaseSelectViewModel[] {
    if (!this.changeReasons) return [];

    const filterValue = value.toString().toLowerCase();
    return this.changeReasons.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  displayChangeReasonFn(value: any): string {
    if (!value) return '';
    const changeReason = this.changeReasons.find(cr => cr.id === value);
    return changeReason ? changeReason.name : value.toString();
  }
}
